import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import type { FormField } from '../../../interfaces/form.interface';
import { InputType } from '../../../interfaces/input.interface';
import { SPANISH_ID_ALLOWED_CHAR_PATTERN } from '../../../utils/spanish-id.keyfilter';
import { normalizeId, SPANISH_ID_LENGTH } from '../../../utils/spanish-id.utils';

@Component({
    selector: 'app-reactive-text-field',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './text-field.component.html',
    styleUrls: ['./text-field.component.scss'],
})
export class ReactiveTextFieldComponent<T> {
    @Input() field!: FormField<T>;

    @Input() model: FormGroup = new FormGroup({});

    @Input() isEditable = false;

    @Input() describedById = '';

    @Input() isInvalid = false;

    public readonly inputType = InputType;

    public keyFilterFor(): RegExp | 'int' | null | undefined {
        if (this.isSpanishIdField()) {
            return SPANISH_ID_ALLOWED_CHAR_PATTERN;
        }

        const explicit = this.field.input?.textConfiguration?.regex;
        const currentValue = this.model.get(this.field.name)?.value;
        const looksNumeric = currentValue !== '' && currentValue != null && !isNaN(Number(currentValue));

        if ((explicit?.test('1') ?? false) && looksNumeric) {
            return 'int';
        }

        return explicit;
    }

    public maxLengthFor(): number | '' {
        if (this.isSpanishIdField()) {
            return this.field.input.maxLength ?? SPANISH_ID_LENGTH;
        }

        return this.field.input.maxLength ?? '';
    }

    public handleTextInput(event: Event): void {
        if (!this.isSpanishIdField()) {
            return;
        }

        const inputElement = event.target as HTMLInputElement | null;
        const control = this.model.get(this.field.name);

        if (!inputElement || !control) {
            return;
        }

        const sanitizedValue = this.sanitizeSpanishId(inputElement.value);

        if (inputElement.value !== sanitizedValue) {
            inputElement.value = sanitizedValue;
            control.setValue(sanitizedValue);
        }
    }

    public handleTextBlur(): void {
        if (!this.isSpanishIdField()) {
            return;
        }

        const control = this.model.get(this.field.name);

        if (!control) {
            return;
        }

        const sanitizedValue = this.sanitizeSpanishId(String(control.value ?? ''));

        if (control.value !== sanitizedValue) {
            control.setValue(sanitizedValue);
        }

        control.markAsTouched();
    }

    public isSpanishIdField(): boolean {
        const fieldName = this.field?.name?.toLowerCase() ?? '';
        const fieldLabel = this.field?.label?.toLowerCase() ?? '';

        return fieldName.includes('nif')
            || fieldName.includes('cif')
            || fieldLabel.includes('nif')
            || fieldLabel.includes('cif');
    }

    public hasTwoFieldConcat(): boolean {
        const cfg = this.field.input.twoFieldsConcatConfiguration;

        return !!cfg
            && !!this.model.get(cfg.subField1)
            && !!this.model.get(cfg.subField2);
    }

    private sanitizeSpanishId(value: string): string {
        const maxLength = this.field.input.maxLength ?? SPANISH_ID_LENGTH;

        return normalizeId(value)
            .replace(/[^0-9A-Z]/g, '')
            .slice(0, maxLength);
    }
}
